import React, {createContext, useState, useCallback, useMemo, useContext, useEffect} from 'react';
import { equipmentService } from '../services/equipmentService';
import {pathianDataApiService} from "../services/PathianDataApiService";

export const EquipmentContext = createContext();

export const EquipmentProvider = props => {
    const [equipmentList, setEquipmentList] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(0);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [sortBy, setSortBy] = useState('num');
    const [orderBy, setOrderBy] = useState('ASC');
    const [sortState, setSortState] = useState([{ fieldName: 'CompanyName', dir: 'asc' }]);
    const [err, setErr] = useState(null);
    const [energySummary , setEnergySummary] = useState(null);
    const [energySummaryTable, setEnergySummaryTable] = useState([]);
    const [equipmentPoints, setEquipmentPoints] = useState([]);
    const [selectedEnergyPoints, setSelectedEnergyPoints] = useState(null);
    const [selectedDetailPoints, setSelectedDetailPoints] = useState([]);
    const [energyPointConsumptionGraph, setEnergyPointConsumptionGraph] = useState({});
    const [energyPointDifferenceGraph, setEnergyPointDifferenceGraph] = useState({});

    const getList = useCallback(
        async (customerId) => {
            const result = await equipmentService.getList(
                customerId,
                pageSize,
                pageIndex,
                sortBy,
                orderBy
            );
            if (result.error) {
                setErr(result.message);
            } else {
                setEquipmentList(result.data);
                setPages(result.page_count);
                setTotal(result.total);
            }
        },
        [pageSize, pageIndex, sortBy, orderBy]
    );

    const getEnergySummary = useCallback(
        async (equipmentIds, startDate, endDate, benchmarkStartDate, benchmarkEndDate, electricRate, thermalRate) => {
            const result = await pathianDataApiService.getEnergySummary(equipmentIds, startDate, endDate, benchmarkStartDate, benchmarkEndDate, electricRate, thermalRate);
            if (result.error) {
                setErr(result.message);
            } else {
                setEnergySummary(result);
            }
        }, []
    );

    const viewEquipmentPoints = useCallback(async (equipmentId) => {
        const result = await equipmentService.viewEquipmentPoints(null, equipmentId);
        setEquipmentPoints(result);
        console.log('stuff', result.filter(point => point.pointType === 'EP'))
        setSelectedEnergyPoints(result.filter(point => point.pointType === 'EP'));
        return result;
    }, []);

    useEffect(() => {
        if (!energySummary) return;
        const tempEnergySummaryTable = [];
        tempEnergySummaryTable.push({
            title: 'Benchmark Consumption',
            electricalValue: `${energySummary.sumBenchmark.electricalLoad.value.toFixed(2)} ${energySummary.sumBenchmark.electricalLoad.unit}`,
            thermalValue: `${energySummary.sumBenchmark.thermalLoad.value.toFixed(2)} ${energySummary.sumBenchmark.thermalLoad.unit}`,
            totalValue: `${(energySummary.sumBenchmark.electricalLoad.value * 0.003412 + energySummary.sumBenchmark.thermalLoad.value * 1.038).toFixed(2)} MMBTU`
        });
        tempEnergySummaryTable.push({
            title: 'Reported Consumption',
            electricalValue: `${energySummary.sumReport.electricalLoad.value.toFixed(2)} ${energySummary.sumReport.electricalLoad.unit}`,
            thermalValue: `${energySummary.sumReport.thermalLoad.value.toFixed(2)} ${energySummary.sumReport.thermalLoad.unit}`,
            totalValue: `${(energySummary.sumReport.electricalLoad.value * 0.003412 + energySummary.sumReport.thermalLoad.value * 1.038).toFixed(2)} MMBTU`
        });
        tempEnergySummaryTable.push({
            title: 'Difference',
            electricalValue: `${energySummary.sumDifference.electricalLoad.value.toFixed(2)} ${energySummary.sumDifference.electricalLoad.unit}`,
            thermalValue: `${energySummary.sumDifference.thermalLoad.value.toFixed(2) } ${energySummary.sumDifference.thermalLoad.unit}`,
            totalValue: `${(energySummary.sumDifference.electricalLoad.value * 0.003412 + energySummary.sumDifference.thermalLoad.value * 1.038).toFixed(2)} MMBTU`
        });
        tempEnergySummaryTable.push({
            title: 'Percent Difference',
            electricalValue: `${100 * Math.abs((energySummary.sumReport.electricalLoad.value - energySummary.sumBenchmark.electricalLoad.value) / ((energySummary.sumReport.electricalLoad.value + energySummary.sumBenchmark.electricalLoad.value) / 2)).toFixed(2)}%`,
            thermalValue: `${100 * Math.abs((energySummary.sumReport.thermalLoad.value - energySummary.sumBenchmark.thermalLoad.value) / ((energySummary.sumReport.thermalLoad.value + energySummary.sumBenchmark.thermalLoad.value) / 2)).toFixed(2)}%`,
            totalValue: ``
        });
        tempEnergySummaryTable.push({
            title: 'Dollars',
            electricalValue: (energySummary.sumDifference.electricalLoad.value * energySummary.electricRate).toLocaleString('en-US', {style: 'currency', currency: 'USD'}),
            thermalValue: (energySummary.sumDifference.thermalLoad.value * energySummary.thermalRate).toLocaleString('en-US', {style: 'currency', currency: 'USD'}),
            totalValue: ((energySummary.sumDifference.electricalLoad.value * energySummary.electricRate) + (energySummary.sumDifference.thermalLoad.value * energySummary.thermalRate)).toLocaleString('en-US', {style: 'currency', currency: 'USD'})
        });
        setEnergySummaryTable(tempEnergySummaryTable);
    }, [energySummary]);

    const getData = useCallback(
        async (equipmentIds, startDate, endDate, benchmarkStartDate, benchmarkEndDate, xAxis, difference) => {
            return await pathianDataApiService.getData(equipmentIds, startDate, endDate, benchmarkStartDate, benchmarkEndDate, xAxis, difference);
        }, []
    );

    const getCalculatedData = useCallback(
        async (equipmentId, functionId, startDate, endDate, benchmarkStartDate, benchmarkEndDate, xAxis) => {
            return await pathianDataApiService.getCalculatedData(equipmentId, functionId, startDate, endDate, benchmarkStartDate, benchmarkEndDate, xAxis);
        }, []
    );

    const values = useMemo(
        () => ({
            equipmentList,
            total,
            pageSize,
            pageIndex,
            sortBy,
            orderBy,
            sortState,
            getList,
            setTotal,
            setPageSize,
            setPageIndex,
            setSortBy,
            setOrderBy,
            setSortState,
            err,
            getEnergySummary,
            energySummary,
            energySummaryTable,
            getData,
            getCalculatedData,
            setEnergyPointConsumptionGraph,
            viewEquipmentPoints,
            selectedEnergyPoints,
            energyPointConsumptionGraph,
            setErr
        }),
        [equipmentList, pageSize, pageIndex, sortState, total, setPageSize, setPageIndex, setSortBy, setOrderBy, setSortState, err, getEnergySummary, energySummary, energySummaryTable, getData, getCalculatedData, setEnergyPointConsumptionGraph, viewEquipmentPoints, selectedEnergyPoints, energyPointConsumptionGraph, setEnergyPointDifferenceGraph, energyPointDifferenceGraph, setErr]
    );

    return <EquipmentContext.Provider value={values}>{props.children}</EquipmentContext.Provider>;
};

export const useEquipment = () => {
    const context = useContext(EquipmentContext);

    if (context === undefined) {
        throw new Error('useEquipment must be used within an EquipmentProvider component.');
    }

    return context;
};
